import * as Yup from "yup";
import i18next from "../../i18n";

/**
 * One tax-document row managed by `FilesForm`'s `useFieldArray`. Mirrors `CompanyFileRequest`
 * (`types/company.type.ts`) minus `type` — this form only ever creates `FileType.TAX_DOCUMENTS`
 * rows (the pre-Carbon component hardcoded `type: FileType.TAX_DOCUMENTS` on every row and never
 * exposed a type control), so `type` is set programmatically in `FilesForm.tsx#onSubmit` rather
 * than carried in the row's own Yup shape/RHF fields — same treatment
 * `bankingInformationValidationSchema.ts` gave the phantom `bankIdentifier` field (Task 9).
 *
 * The pre-Carbon form also never exposed `name`/`description` text inputs to the user — `name`
 * was silently set from the uploaded `File`'s own `.name` the moment an upload completed
 * (`if (!newFile.name) setNewFile({ ...newFile, name: file.name })`), and `description` stayed ""
 * forever (no control ever wrote to it). Preserved 1:1: `FileRow.tsx` sets `name` the same way via
 * `setValue`, and neither field gets a visible control here — introducing one now would be new
 * UI, not a migration.
 */
export interface FileFormFields {
  fileURL: string;
  name: string;
  description: string;
}

export interface FilesFormFields {
  files: FileFormFields[];
}

const maxLengthMessage = (max: number) =>
  i18next.t("validations.maxLength", {
    max,
    defaultValue: `Must not exceed ${max} characters`,
  });

/**
 * `fileURL` is `.required()` — reproducing the pre-Carbon "Add" button's own disabled condition
 * EXACTLY (`disabled={isUploading || !newFile.fileURL}`): no tax document was ever staged without
 * a completed upload, so every already-persisted row already satisfies this. `.max(2000)` mirrors
 * `CompanyProfileUpdateRequest.FileRequest.fileURL`'s own `@Size(max = 2000)`.
 *
 * `name`/`description` carry only max-length mirrors of the backend's own `@Size` constraints
 * (`name` 255, `description` 1000) — neither was ever required pre-Carbon (no control ever let a
 * user type either), so no `.required()` is added for them here either.
 */
export const fileRowValidationSchema = Yup.object({
  fileURL: Yup.string()
    .required(i18next.t("companyProfile.fileRequired"))
    .max(2000, maxLengthMessage(2000)),
  name: Yup.string().max(255, maxLengthMessage(255)),
  description: Yup.string().max(1000, maxLengthMessage(1000)),
});

/**
 * The pre-Carbon form never validated the LIST itself (zero tax documents was, and stays, a valid
 * state) — only each staged row, at the moment it was added. No `.min()`/`.required()` on the
 * array, same rationale as every other repeatable sub-form's schema (Tasks 8-9).
 */
export const filesValidationSchema = Yup.object({
  files: Yup.array().of(fileRowValidationSchema).default([]),
});
